import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useGalleriesStore } from '../stores/galleriesStore';
import { useDialogStore } from '../stores/dialogStore';
import ProtectedImage from '../components/ProtectedImage';
import ProtectedVideo from '../components/ProtectedVideo';
import ProtectedVideoThumbnail from '../components/ProtectedVideoThumbnail';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable media item for drag-and-drop
const SortableMediaItem = ({ item, galleryId, onView, onDelete, layoutCode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isTiles = layoutCode === 'GALLERY_TILES';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative aspect-square ${isTiles ? '' : 'rounded'} overflow-hidden bg-gray-200 dark:bg-gray-700 group cursor-move`}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          onView(item);
        }}
        className="w-full h-full cursor-pointer hover:opacity-80 transition-opacity"
      >
        {item.kind_code === 'IMAGE' ? (
          <ProtectedImage
            uuid={item.thumbnail_uuid || item.uuid}
            alt={item.storage_key}
            className="w-full h-full object-cover"
          />
        ) : (
          <ProtectedVideoThumbnail
            uuid={item.uuid}
            className="w-full h-full"
          />
        )}
      </div>
      {/* Delete button overlay */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Delete media"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
};

const Galleries = () => {
  const { accessToken, profile } = useAuthStore();
  const { alert, confirm } = useDialogStore();
  const {
    galleries,
    isLoading,
    error,
    fetchGalleries,
    updateGallery,
    deleteGallery,
    addMedia,
    deleteMedia,
    reorderMedia,
    clearError,
  } = useGalleriesStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadingGalleryId, setUploadingGalleryId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAllGalleries, setShowAllGalleries] = useState(false);
  const [carouselIndices, setCarouselIndices] = useState({});
  const [localGalleryMedia, setLocalGalleryMedia] = useState({});

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (accessToken) {
      fetchGalleries(accessToken);
    }
  }, [accessToken]);

  // Sync galleries media to local state for drag-and-drop
  useEffect(() => {
    const newLocalMedia = {};
    galleries.forEach(gallery => {
      if (gallery.media) {
        newLocalMedia[gallery.id] = gallery.media;
      }
    });
    setLocalGalleryMedia(newLocalMedia);
  }, [galleries]);

  // Handle layout change
  const handleLayoutChange = async (galleryId, layoutCode) => {
    const result = await updateGallery(galleryId, { layout_code: layoutCode }, accessToken);
    if (!result.success) {
      await alert(result.error || 'Failed to update layout', 'Error');
    } else {
      // Refresh galleries to get updated layout
      await fetchGalleries(accessToken);
    }
  };

  // Handle drag end for media reordering
  const handleDragEnd = async (event, galleryId) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const media = localGalleryMedia[galleryId] || [];
    const oldIndex = media.findIndex(m => m.id === active.id);
    const newIndex = media.findIndex(m => m.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Update local state optimistically
    const reordered = arrayMove(media, oldIndex, newIndex);
    setLocalGalleryMedia(prev => ({
      ...prev,
      [galleryId]: reordered
    }));

    // Send to backend
    const mediaOrder = reordered.map(m => m.id);
    const result = await reorderMedia(galleryId, mediaOrder, accessToken);

    if (!result.success) {
      // Revert on error
      await alert(result.error || 'Failed to reorder media', 'Error');
      await fetchGalleries(accessToken);
    }
  };

  // Carousel navigation
  const handleCarouselPrev = (galleryId, mediaLength) => {
    setCarouselIndices(prev => ({
      ...prev,
      [galleryId]: (prev[galleryId] || 0) === 0 ? mediaLength - 1 : (prev[galleryId] || 0) - 1
    }));
  };

  const handleCarouselNext = (galleryId, mediaLength) => {
    setCarouselIndices(prev => ({
      ...prev,
      [galleryId]: ((prev[galleryId] || 0) + 1) % mediaLength
    }));
  };

  const handleDeleteGallery = async (galleryId) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this gallery? All media will be permanently deleted.',
      'Delete Gallery',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    const result = await deleteGallery(galleryId, accessToken);
    if (!result.success) {
      await alert(result.error || 'Failed to delete gallery', 'Error');
    }
  };

  const handleAddMedia = async (galleryId, e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const files = Array.from(e.target.files);
    setUploadingGalleryId(galleryId);
    setUploadProgress(0);

    const result = await addMedia(galleryId, files, accessToken);

    if (!result.success) {
      await alert(result.error || 'Failed to upload media', 'Error');
    } else {
      // Refresh galleries to show new media
      await fetchGalleries(accessToken);
    }

    setUploadingGalleryId(null);
    setUploadProgress(0);
    e.target.value = '';
  };

  const handleDeleteMedia = async (galleryId, assetId) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this media?',
      'Delete Media',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    // Close modal if this media is currently open
    if (selectedMedia?.id === assetId) {
      setSelectedMedia(null);
    }

    const result = await deleteMedia(galleryId, assetId, accessToken);
    if (!result.success) {
      await alert(result.error || 'Failed to delete media', 'Error');
    } else {
      // Refresh galleries
      await fetchGalleries(accessToken);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-primary">My Galleries</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          title="Create gallery"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={clearError} className="text-red-900 dark:text-red-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && galleries.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-secondary">Loading galleries...</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && galleries.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-secondary">No galleries yet</p>
        </div>
      )}

      {/* Galleries list */}
      <div className="space-y-8">
        {(showAllGalleries ? galleries : galleries.slice(0, 6)).map((gallery) => (
          <div key={gallery.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            {/* Title, Description, and Delete button */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-primary mb-2">TESTING 123 - {gallery.title}</h2>
                {gallery.description && (
                  <p className="text-secondary">{gallery.description}</p>
                )}
                {/* DEBUG INFO - REMOVE AFTER TESTING */}
                <div className="bg-yellow-300 text-black p-2 my-2 border-4 border-black">
                  Gallery Owner: {gallery.owner_profile_id} | Current User: {profile?.id} | Match: {String(gallery.owner_profile_id === profile?.id)} | Layout: {gallery.layout_code || 'none'}
                </div>

                {/* Layout switcher - only for gallery owner */}
                {gallery.owner_profile_id === profile?.id && (
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => handleLayoutChange(gallery.id, 'GALLERY_THUMBS')}
                      className={`p-2 rounded ${gallery.layout_code === 'GALLERY_THUMBS' || !gallery.layout_code ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
                      title="Thumbnails - Grid with gaps"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleLayoutChange(gallery.id, 'GALLERY_TILES')}
                      className={`p-2 rounded ${gallery.layout_code === 'GALLERY_TILES' ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
                      title="Tiles - Grid without gaps"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleLayoutChange(gallery.id, 'GALLERY_CAROUSEL')}
                      className={`p-2 rounded ${gallery.layout_code === 'GALLERY_CAROUSEL' ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
                      title="Carousel - Slideshow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDeleteGallery(gallery.id)}
                className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                title="Delete gallery"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>

            {/* Media rendering based on layout */}
            {gallery.media && gallery.media.length > 0 ? (
              <>
                {/* THUMBNAILS Layout - Grid with gaps + drag-and-drop */}
                {(gallery.layout_code === 'GALLERY_THUMBS' || !gallery.layout_code) && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, gallery.id)}
                  >
                    <SortableContext
                      items={(localGalleryMedia[gallery.id] || gallery.media).map(m => m.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                        {(localGalleryMedia[gallery.id] || gallery.media).map((item) => (
                          <SortableMediaItem
                            key={item.id}
                            item={item}
                            galleryId={gallery.id}
                            layoutCode="GALLERY_THUMBS"
                            onView={(item) => setSelectedMedia({ ...item, galleryId: gallery.id })}
                            onDelete={(itemId) => handleDeleteMedia(gallery.id, itemId)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {/* TILES Layout - Grid without gaps + drag-and-drop */}
                {gallery.layout_code === 'GALLERY_TILES' && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, gallery.id)}
                  >
                    <SortableContext
                      items={(localGalleryMedia[gallery.id] || gallery.media).map(m => m.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-0 mb-4">
                        {(localGalleryMedia[gallery.id] || gallery.media).map((item) => (
                          <SortableMediaItem
                            key={item.id}
                            item={item}
                            galleryId={gallery.id}
                            layoutCode="GALLERY_TILES"
                            onView={(item) => setSelectedMedia({ ...item, galleryId: gallery.id })}
                            onDelete={(itemId) => handleDeleteMedia(gallery.id, itemId)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {/* CAROUSEL Layout - Slideshow (no drag-and-drop) */}
                {gallery.layout_code === 'GALLERY_CAROUSEL' && (
                  <div className="mb-4">
                    <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                      {/* Current slide */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {gallery.media[carouselIndices[gallery.id] || 0]?.kind_code === 'IMAGE' ? (
                          <ProtectedImage
                            uuid={gallery.media[carouselIndices[gallery.id] || 0]?.uuid}
                            alt="Carousel image"
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <ProtectedVideo
                            uuid={gallery.media[carouselIndices[gallery.id] || 0]?.uuid}
                            className="max-w-full max-h-full"
                          />
                        )}
                      </div>

                      {/* Navigation arrows */}
                      {gallery.media.length > 1 && (
                        <>
                          <button
                            onClick={() => handleCarouselPrev(gallery.id, gallery.media.length)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCarouselNext(gallery.id, gallery.media.length)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Delete button for current slide */}
                      <button
                        onClick={() => handleDeleteMedia(gallery.id, gallery.media[carouselIndices[gallery.id] || 0]?.id)}
                        className="absolute top-4 right-4 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Delete current media"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Slide indicators - below the carousel */}
                    <div className="flex justify-center gap-2 mt-3">
                      {gallery.media.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCarouselIndices(prev => ({ ...prev, [gallery.id]: index }))}
                          className={`w-2 h-2 rounded-full transition-all ${
                            (carouselIndices[gallery.id] || 0) === index
                              ? 'bg-primary-600 w-8'
                              : 'bg-primary-600 bg-opacity-50'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-secondary text-center py-8">No media uploaded</p>
            )}

            {/* Upload button */}
            <div className="flex justify-end">
              {uploadingGalleryId === gallery.id ? (
                <div className="w-full max-w-xs">
                  <div className="text-sm text-secondary mb-2">Uploading...</div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                </div>
              ) : (
                <label className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 cursor-pointer transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => handleAddMedia(gallery.id, e)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Show more galleries button */}
      {!showAllGalleries && galleries.length > 6 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowAllGalleries(true)}
            className="px-6 py-3 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium border border-primary-600 dark:border-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-colors"
          >
            Show {galleries.length - 6} more {galleries.length - 6 === 1 ? 'gallery' : 'galleries'}
          </button>
        </div>
      )}

      {showAllGalleries && galleries.length > 6 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowAllGalleries(false)}
            className="px-6 py-3 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
          >
            Show less
          </button>
        </div>
      )}

      {/* Create Gallery Modal */}
      {showCreateModal && (
        <CreateGalleryModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchGalleries(accessToken);
          }}
        />
      )}

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <MediaViewerModal
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
};

// Create Gallery Modal Component
const CreateGalleryModal = ({ onClose, onCreated }) => {
  const { accessToken } = useAuthStore();
  const { createGallery } = useGalleriesStore();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    visibility_code: 'MEMBERS',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const result = await createGallery(formData, accessToken);

    if (result.success) {
      onCreated(result.gallery);
    } else {
      setError(result.error || 'Failed to create gallery');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-semibold text-primary mb-4">Create New Gallery</h3>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-secondary mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-primary"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-secondary mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-primary"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary mb-2">
              Visibility
            </label>
            <select
              value={formData.visibility_code}
              onChange={(e) => setFormData({ ...formData, visibility_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-primary"
            >
              <option value="PRIVATE">Private (Only me and my Masters)</option>
              <option value="MEMBERS">Members (All registered users)</option>
              <option value="PUBLIC">Public (Everyone)</option>
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Gallery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Media Viewer Modal Component
const MediaViewerModal = ({ media, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 z-10"
          title="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Media content */}
        <div className="bg-black rounded-lg overflow-hidden">
          {media.kind_code === 'IMAGE' ? (
            <ProtectedImage
              uuid={media.uuid}
              alt={media.storage_key}
              className="w-full h-auto max-h-screen object-contain"
            />
          ) : (
            <ProtectedVideo
              uuid={media.uuid}
              controls={true}
              autoPlay={true}
              className="w-full h-auto max-h-screen"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Galleries;
